// Shared SEMANTIC position-anchor registry.
// Coordinates are normalized 0..1 in the LineupEditor2 system: y=0 is TOP
// (attacking), y=1 is BOTTOM (GK). IDs + labels match the mobile tactical-slot
// system (lowercase) so a lineup's formation_point_id is cross-platform safe.
//
// A "formation" is only a chosen SUBSET/layout of these anchors — coaches can
// move a player to ANY relevant anchor without changing the formation engine.

const A = (id, label, x, y, family) => ({ id, label, x, y, family });

// Canonical library (24 anchors) — same ids/labels as mobile TACTICAL_SLOTS.
export const ALL_ANCHORS = [
  A('gk', 'MV', 0.50, 0.92, 'GK'),
  // Defence
  A('lb', 'VB', 0.12, 0.78, 'FB_WB'),
  A('lcb', 'VMB', 0.34, 0.80, 'CB'),
  A('cb', 'MB', 0.50, 0.80, 'CB'),
  A('rcb', 'HMB', 0.66, 0.80, 'CB'),
  A('rb', 'HB', 0.88, 0.78, 'FB_WB'),
  // Defensive midfield + wingbacks
  A('lwb', 'VYB', 0.10, 0.64, 'FB_WB'),
  A('ldm', 'VDM', 0.32, 0.64, 'DM'),
  A('cdm', 'CDM', 0.50, 0.64, 'DM'),
  A('rdm', 'HDM', 0.68, 0.64, 'DM'),
  A('rwb', 'HYB', 0.90, 0.64, 'FB_WB'),
  // Central midfield + wide mids
  A('lm', 'VM', 0.10, 0.50, 'WINGER'),
  A('lcm', 'VIM', 0.32, 0.50, 'CM'),
  A('cm', 'CM', 0.50, 0.50, 'CM'),
  A('rcm', 'HIM', 0.68, 0.50, 'CM'),
  A('rm', 'HM', 0.90, 0.50, 'WINGER'),
  // Attacking midfield
  A('lam', 'VAM', 0.28, 0.36, 'AM'),
  A('cam', 'CAM', 0.50, 0.36, 'AM'),
  A('ram', 'HAM', 0.72, 0.36, 'AM'),
  // Attack + wingers
  A('lw', 'VY', 0.12, 0.22, 'WINGER'),
  A('lf', 'VF', 0.30, 0.22, 'ST'),
  A('st', 'ST', 0.50, 0.22, 'ST'),
  A('rf', 'HF', 0.70, 0.22, 'ST'),
  A('rw', 'HY', 0.88, 0.22, 'WINGER'),
];

export const ANCHOR_BY_ID = ALL_ANCHORS.reduce((acc, a) => { acc[a.id] = a; return acc; }, {});

// Format-appropriate anchor subsets (fewer points on smaller pitches).
const FORMAT_ANCHOR_IDS = {
  '11v11': ALL_ANCHORS.map((a) => a.id), // ~24
  '9v9': ['gk', 'lb', 'lcb', 'cb', 'rcb', 'rb', 'lwb', 'rwb', 'cdm', 'lm', 'lcm', 'cm', 'rcm', 'rm', 'cam', 'lw', 'lf', 'st', 'rf', 'rw'], // 20
  '7v7': ['gk', 'lb', 'lcb', 'cb', 'rcb', 'rb', 'cdm', 'lm', 'cm', 'rm', 'cam', 'lf', 'st', 'rf'], // 14
  '5v5': ['gk', 'lb', 'cb', 'rb', 'lm', 'cm', 'rm', 'cam', 'st'], // 9
};

export const getPositionAnchors = (gameFormat) =>
  (FORMAT_ANCHOR_IDS[gameFormat] || FORMAT_ANCHOR_IDS['11v11']).map((id) => ANCHOR_BY_ID[id]).filter(Boolean);

// Resolve a display label for a semantic anchor id (case-insensitive).
export const getAnchorLabel = (id) => (id ? (ANCHOR_BY_ID[String(id).toLowerCase()]?.label || null) : null);

// Position family for an anchor id (case-insensitive).
export const getAnchorFamily = (id) => (id ? (ANCHOR_BY_ID[String(id).toLowerCase()]?.family || null) : null);
